// src/pages/account/ProfilePage.jsx
import React, { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Sidebar from '../../components/DashboardSidebar';
import { Menu } from 'lucide-react';

export default function ProfilePage() {
  const auth = useContext(AuthContext);
  const token = auth?.user?.token;

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    language: 'en',
    company: '',
    address: '',
    phone: ''
  });
  const [lang, setLang] = useState('en');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [errors, setErrors] = useState({});

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordErrors, setPasswordErrors] = useState({});

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE}/api/users/me`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if (!res.ok) throw new Error('Failed to fetch profile');
        const data = await res.json();
        setUser(data);
        setLang(data.language || 'en');
        setFormData({
          name: data.name || '',
          language: data.language || 'en',
          company: data.company || '',
          address: data.address || '',
          phone: data.phone || ''
        });
      } catch (err) {
        toast.error('Failed to load profile');
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchProfile();
  }, [token]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Name is required';
    if (formData.phone && !/^[0-9+\-() ]{7,20}$/.test(formData.phone)) {
      errs.phone = 'Invalid phone number';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validatePassword = () => {
    const errs = {};
    if (!passwordData.currentPassword) errs.currentPassword = 'Current password required';
    if (!passwordData.newPassword) errs.newPassword = 'New password required';
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      errs.confirmPassword = 'Passwords do not match';
    }
    setPasswordErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE}/api/users/update-profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || 'Update failed');
      toast.success('Profile updated successfully');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!validatePassword()) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE}/api/users/change-password`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || 'Password change failed');
      toast.success('Password changed successfully');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (!user) return <div className="p-6 text-red-500">User not found or failed to load.</div>;

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'block' : 'hidden'} fixed md:relative md:block z-40 w-64`}>
        <Sidebar currentLang={lang} activeSection="account"
          onLangChange={setLang}
          onCloseMobile={() => setSidebarOpen(false)}
        />
      </div>

      {/* Main content */}
      <div className="flex-1 p-6 bg-gray-100 min-h-screen">
        <ToastContainer position="top-right" />

        {/* Hamburger for mobile */}
        <button onClick={() => setSidebarOpen(true)} className="block md:hidden mb-4">
          <Menu className="w-6 h-6 text-gray-700" />
        </button>

        <h2 className="text-3xl font-semibold mb-4 text-gray-800">My Account</h2>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Profile Info */}
          
          <div className="lg:col-span-7 space-y-6">
          <h3 className="text-lg font-semibold mb-2">Manage Profile</h3>
            {/* Profile form fields (same as before) */}
            <div>
              <label className="block text-sm font-medium">Name</label>
              <input type="text" name="name" value={formData.name} onChange={handleChange} className={`w-full border px-3 py-2 rounded mt-1 ${errors.name ? 'border-red-500' : ''}`} />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium">Email</label>
              <input type="text" value={user?.email || ''} disabled className="w-full bg-gray-100 border px-3 py-2 rounded mt-1" />
            </div>
            <div>
              <label className="block text-sm font-medium">Country</label>
              <input type="text" value={user?.countryName || ''} disabled className="w-full bg-gray-100 border px-3 py-2 rounded mt-1" />
            </div>
            <div>
              <label className="block text-sm font-medium">Language</label>
              <select name="language" value={formData.language} onChange={handleChange} className="w-full border px-3 py-2 rounded mt-1">
                <option value="en">English</option>
                <option value="ar">Arabic</option>
                <option value="hi">Hindi</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium">Company</label>
              <input type="text" name="company" value={formData.company} onChange={handleChange} className="w-full border px-3 py-2 rounded mt-1" />
            </div>
            <div>
              <label className="block text-sm font-medium">Address</label>
              <input type="text" name="address" value={formData.address} onChange={handleChange} className="w-full border px-3 py-2 rounded mt-1" />
            </div>
            <div>
              <label className="block text-sm font-medium">Phone</label>
              <input type="text" name="phone" value={formData.phone} onChange={handleChange} className={`w-full border px-3 py-2 rounded mt-1 ${errors.phone ? 'border-red-500' : ''}`} />
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            </div>
            <button onClick={handleSubmit} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">Update Profile</button>
          </div>

          {/* Change Password Section */}
          <div className="lg:col-span-4 border-l pl-6">
            <h3 className="text-lg font-semibold mb-2">Change Password</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium">Current Password</label>
                <input type="password" name="currentPassword" value={passwordData.currentPassword} onChange={handlePasswordChange} className={`w-full border px-3 py-2 rounded mt-1 ${passwordErrors.currentPassword ? 'border-red-500' : ''}`} />
                {passwordErrors.currentPassword && <p className="text-red-500 text-xs mt-1">{passwordErrors.currentPassword}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium">New Password</label>
                <input type="password" name="newPassword" value={passwordData.newPassword} onChange={handlePasswordChange} className={`w-full border px-3 py-2 rounded mt-1 ${passwordErrors.newPassword ? 'border-red-500' : ''}`} />
                {passwordErrors.newPassword && <p className="text-red-500 text-xs mt-1">{passwordErrors.newPassword}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium">Confirm New Password</label>
                <input type="password" name="confirmPassword" value={passwordData.confirmPassword} onChange={handlePasswordChange} className={`w-full border px-3 py-2 rounded mt-1 ${passwordErrors.confirmPassword ? 'border-red-500' : ''}`} />
                {passwordErrors.confirmPassword && <p className="text-red-500 text-xs mt-1">{passwordErrors.confirmPassword}</p>}
              </div>
              <button onClick={handlePasswordSubmit} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700">Change Password</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
